/**
 * Générateur — Reconnaître un nombre décimal
 * Attendu ID : 599 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte, texte_trou_drag,
 *                 texte_trou_saisie, relier, classer, tableau_vf
 * Labels de référence :
 *   - Convertir une fraction décimale en écriture à virgule
 *   - Convertir une écriture à virgule en fraction décimale
 *   - Identifier si un nombre est décimal ou non
 * Variantes estimées : >500
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
    generateDistractors, tex, fracTex,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 599;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Pools de données ───
const DENOMINATEURS = [10, 100, 1000];

// Fractions non décimales (pour l'exercice "identifier")
const NON_DECIMAUX = [
    { num: 1, den: 3 },
    { num: 2, den: 7 },
    { num: 5, den: 6 },
    { num: 1, den: 6 },
    { num: 3, den: 7 },
    { num: 4, den: 9 },
    { num: 1, den: 9 },
    { num: 2, den: 3 },
    { num: 5, den: 7 },
    { num: 1, den: 11 },
];

// ─── Helpers spécifiques ───

/** Génère une fraction décimale aléatoire { num, den, decimal } */
const randomFracDecimale = () => {
    const den = randomChoice(DENOMINATEURS);
    const maxNum = den === 10 ? 999 : den === 100 ? 9999 : 9999;
    const num = randInt(1, maxNum);
    const decimal = (num / den).toFixed(den === 10 ? 1 : den === 100 ? 2 : 3)
        .replace(/\.?0+$/, '') // retirer les zéros finaux inutiles sauf besoin
        || '0';
    return { num, den, decimal: String(num / den) };
};

/** Formate un décimal proprement (virgule française).
 *  Arrondit à 10 décimales pour neutraliser les artifacts floating-point
 *  générés par les calculs de distracteurs (ex: 1.9 + 0.3 = 2.1999999999999997). */
const formatDecimal = (n) => {
    const rounded = Math.round(Number(n) * 1e10) / 1e10;
    return String(rounded).replace('.', ',');
};

/** Expression KaTeX pour une fraction */
const fracKatex = (num, den) => `\\frac{${num}}{${den}}`;

/** Décimal DÉJÀ FORMATÉ (« 0,3 ») dans une formule KaTeX : la virgule
 *  décimale s'écrit `{,}` (sans espace après, contrairement à la virgule de
 *  ponctuation). */
const decTex = (s) => String(s).replace(',', '{,}');

/** Ligne de tableau ou étiquette « fraction = décimal », tout en KaTeX
 *  (audit fractions en texte, 2026-09-06 : plus jamais « 3/10 » à l'écran). */
const egaliteTex = (num, den, decimal) => tex(`\\dfrac{${num}}{${den}} = ${decTex(decimal)}`);

// Trou dans une fraction d'une phrase à trous (ligne KaTeX globale) : les
// `\rule` invisibles donnent au slot la hauteur d'un numérateur ou d'un
// dénominateur, motif des générateurs 620-622. Triple accolade `{{{X}}}`
// décodée par preprocessBraces (texteTrouMath.js).
const fracTrouNum = (num, den) => `\\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{${den}}`;
const fracTrouDen = (num, den) => `\\dfrac{${num}}{\\rule{0pt}{1.3em}{{${den}}}}`;

/** Élément expression KaTeX */
const exprElement = (contenu) => ({
    type: 'expression',
    config: { contenu },
});

// ─── Labels ───
const L1 = 'Convertir une fraction décimale en écriture à virgule';
const L2 = 'Convertir une écriture à virgule en fraction décimale';
const L3 = 'Identifier si un nombre est décimal ou non';

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 : Convertir fraction décimale → écriture à virgule
// ═══════════════════════════════════════════════════════════

/** QCM unique — "Quelle est l'écriture décimale de a/b ?" */
const genConvertirFracQcm = () => {
    const den = randomChoice(DENOMINATEURS);
    // Choisir un numérateur qui donne un piège de rang intéressant
    const num = den === 10 ? randInt(1, 99) : den === 100 ? randInt(1, 999) : randInt(1, 999);
    const correct = formatDecimal(num / den);

    // Distracteurs : erreurs de rang
    const distracteurs = [];
    // Piège 1 : diviser par le dénominateur ×10 (un rang de trop)
    distracteurs.push(formatDecimal(num / (den * 10)));
    // Piège 2 : diviser par le dénominateur /10 (un rang de moins)
    if (den >= 100) distracteurs.push(formatDecimal(num / (den / 10)));
    else distracteurs.push(formatDecimal(num / 1));
    // Piège 3 : inverser (résultat très différent)
    distracteurs.push(formatDecimal(num * 10 / den));

    // Dédoublonner et s'assurer qu'on a 3 distracteurs distincts de la bonne réponse
    const uniqueDistracteurs = [...new Set(distracteurs)].filter(d => d !== correct);
    // Secours : candidat accepté seulement s'il est NOUVEAU (le tirage num=10,
    // den=100 donnait pièges [0,01 ; 1 ; 1] puis un secours 0,1+0,9 = 1 → doublon)
    for (let k = 1; k <= 9 && uniqueDistracteurs.length < 3; k++) {
        const cand = formatDecimal(num / den + k / 10);
        if (cand !== correct && !uniqueDistracteurs.includes(cand)) uniqueDistracteurs.push(cand);
    }
    // Repli déterministe (décalages entiers, distincts par construction)
    for (let k = 1; uniqueDistracteurs.length < 3; k++) {
        const cand = formatDecimal(num / den + k);
        if (cand !== correct && !uniqueDistracteurs.includes(cand)) uniqueDistracteurs.push(cand);
    }
    const choix = [correct, ...uniqueDistracteurs.slice(0, 3)];

    return makeQcm(
        choix,
        correct,
        `<p>Quelle est l'écriture décimale de {{element:0}} ?</p>`,
        {
            ...opts(L1),
            elements: { version: 1, elements: [exprElement(fracKatex(num, den))] },
        },
    );
};

/** Saisie — "Écris sous forme décimale : a/b = ..." */
const genConvertirFracSaisie = () => {
    const den = randomChoice(DENOMINATEURS);
    const num = den === 10 ? randInt(1, 999) : den === 100 ? randInt(1, 999) : randInt(1, 999);
    const correct = formatDecimal(num / den);

    const payload = makeSaisie(
        correct,
        `<p>Écris sous forme décimale : {{element:0}} = ...</p>`,
        opts(L1),
    );
    payload.elements_additionnels = {
        version: 1,
        elements: [exprElement(fracKatex(num, den))],
    };
    return payload;
};

/** Tableau V/F — égalités fraction = décimal vraies ou fausses */
const genConvertirFracVF = () => {
    const lignes = [];
    const reponses = [];

    for (let i = 0; i < 5; i++) {
        const den = randomChoice(DENOMINATEURS);
        const num = randInt(1, den === 10 ? 99 : 999);
        const correctDecimal = formatDecimal(num / den);
        const isVrai = Math.random() < 0.5;

        if (isVrai) {
            lignes.push(egaliteTex(num, den, correctDecimal));
            reponses.push(0); // Vrai
        } else {
            // Créer un faux en décalant d'un rang
            const faux = formatDecimal(num / (den * 10));
            lignes.push(egaliteTex(num, den, faux));
            reponses.push(1); // Faux
        }
    }

    return makeVraiFaux(
        lignes,
        reponses,
        '<p>Indique si chaque égalité est vraie ou fausse.</p>{{element:0}}',
        opts(L1),
    );
};

/** Relier — fraction décimale ↔ écriture à virgule */
const genConvertirFracRelier = () => {
    // Générer 4-5 paires avec au moins 2 pièges de rang (même numérateur, dénominateurs différents)
    const nums = randomChoiceMultiple([3, 7, 9, 12, 25, 45, 8, 15, 6, 17], 4);
    const pairs = [];

    // Paire piège 1 : même numérateur, /10 et /100
    const n1 = nums[0];
    pairs.push({ source: fracTex(n1, 10), target: formatDecimal(n1 / 10) });
    pairs.push({ source: fracTex(n1, 100), target: formatDecimal(n1 / 100) });

    // 2 paires normales
    const n2 = nums[2];
    const d2 = randomChoice([10, 100, 1000]);
    pairs.push({ source: fracTex(n2, d2), target: formatDecimal(n2 / d2) });

    const n3 = nums[3];
    const d3 = randomChoice([10, 100]);
    pairs.push({ source: fracTex(n3, d3), target: formatDecimal(n3 / d3) });

    return makeRelier(
        pairs,
        '<p>Relie chaque fraction décimale à son écriture à virgule.</p>',
        opts(L1),
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 : Convertir écriture à virgule → fraction décimale
// ═══════════════════════════════════════════════════════════

/** QCM unique — "Quelle fraction décimale est égale à 0,xxx ?" */
const genConvertirDecQcm = () => {
    const den = randomChoice(DENOMINATEURS);
    const num = den === 10 ? randInt(1, 99) : den === 100 ? randInt(1, 999) : randInt(1, 999);
    const decimal = formatDecimal(num / den);

    const correct = { type: 'expression', config: { contenu: fracKatex(num, den) } };

    // Distracteurs : mauvais dénominateur
    const distracteurs = [];
    for (const d of DENOMINATEURS) {
        if (d !== den) {
            distracteurs.push({ type: 'expression', config: { contenu: fracKatex(num, d) } });
        }
    }
    // Ajouter un piège avec numérateur ×10
    distracteurs.push({ type: 'expression', config: { contenu: fracKatex(num * 10, den) } });

    const choix = [correct, ...distracteurs.slice(0, 3)];

    return makeQcm(
        choix,
        correct,
        `<p>Quelle fraction décimale est égale à ${decimal} ?</p>`,
        opts(L2),
    );
};

/** Texte à trous saisie — "0,140 = .../100" */
const genConvertirDecSaisie = () => {
    const den = randomChoice(DENOMINATEURS);
    const num = den === 10 ? randInt(1, 99) : den === 100 ? randInt(1, 999) : randInt(1, 999);
    const decimal = formatDecimal(num / den);

    // Cacher le numérateur OU le dénominateur aléatoirement
    const hideNum = Math.random() < 0.5;

    // Ligne KaTeX globale : la fraction est verticale, le trou dans le
    // numérateur ou le dénominateur ; l'élève tape le nombre au clavier.
    const phrase = hideNum
        ? `$${decTex(decimal)} = ${fracTrouNum(num, den)}$`
        : `$${fracTrouDen(num, den)} = ${decTex(decimal)}$`;

    return makeTexteTrou(phrase, '<p>Complète avec le bon nombre.</p>', { ...opts(L2), mode: 'saisie' });
};

/** Texte à trous drag — une conversion avec distracteurs */
const genConvertirDecDrag = () => {
    const den = randomChoice(DENOMINATEURS);
    const num = den === 10 ? randInt(1, 99) : den === 100 ? randInt(1, 999) : randInt(1, 999);
    const decimal = formatDecimal(num / den);

    // Phrase : soit on cache le décimal, soit le numérateur
    const hideDecimal = Math.random() < 0.5;

    let phrase, distracteurs;
    if (hideDecimal) {
        phrase = `$\\dfrac{${num}}{${den}} = {{${decimal}}}$`;
        // Distracteurs : erreurs de rang
        distracteurs = [
            formatDecimal(num / (den * 10)),
            formatDecimal(num / (den / 10 || 1)),
            formatDecimal(num * 10 / den),
        ];
    } else {
        phrase = `$${decTex(decimal)} = ${fracTrouNum(num, den)}$`;
        distracteurs = [
            String(num * 10),
            String(Math.round(num / 10) || 1),
            String(num + den),
        ];
    }

    // Dédoublonner
    const uniqueD = [...new Set(distracteurs)].filter(d => d !== String(num) && d !== decimal).slice(0, 3);

    return makeTexteTrou(phrase, '<p>Complète avec le bon nombre.</p>', {
        ...opts(L2),
        mode: 'drag',
        distracteurs: uniqueD,
    });
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 : Identifier si un nombre est décimal ou non
// ═══════════════════════════════════════════════════════════

/** QCM multiple — "Quels nombres sont décimaux ?" */
const genIdentifierQcmMulti = () => {
    // Décimaux : mélange d'écritures
    const decimaux = [];
    // Un entier
    const entier = randInt(1, 50);
    decimaux.push(String(entier));
    // Un décimal à virgule — écriture à virgule GARANTIE (dixièmes 1-9) :
    // randInt(1,99)/10 pouvait retomber sur un entier (80/10 → « 8 ») et
    // dupliquer l'entier ci-dessus (choix identiques dans le QCM).
    decimaux.push(formatDecimal(randInt(0, 9) + randInt(1, 9) / 10));
    // Une fraction décimale
    const d = randomChoice(DENOMINATEURS);
    const n = randInt(1, 99);
    decimaux.push({ type: 'expression', config: { contenu: fracKatex(n, d) } });

    // Non-décimaux
    const nonDec = randomChoiceMultiple(NON_DECIMAUX, 3);
    const nonDecItems = nonDec.map(nd => ({
        type: 'expression',
        config: { contenu: fracKatex(nd.num, nd.den) },
    }));

    const choix = [...decimaux, ...nonDecItems];

    return makeQcm(
        choix,
        decimaux,
        '<p>Parmi les nombres suivants, lesquels sont des nombres décimaux ?</p>',
        opts(L3),
        true, // multi
    );
};

/** Classer — décimal vs non décimal */
const genIdentifierClasser = () => {
    // Items décimaux — re-tirés jusqu'à 4 écritures DISTINCTES : « x,x » peut
    // donner un entier (30/10 → « 3 ») qui dupliquerait l'entier tiré, et les
    // deux fractions peuvent coïncider (n/100 des deux côtés).
    let decItems = [];
    for (let essai = 0; essai < 30 && new Set(decItems).size !== 4; essai++) {
        decItems = [
            formatDecimal(randInt(1, 99) / 10),
            fracTex(randInt(1, 99), randomChoice([10, 100])),
            String(randInt(1, 20)),
            fracTex(randInt(1, 99), randomChoice([100, 1000])),
        ];
    }

    // Items non décimaux
    const nonDec = randomChoiceMultiple(NON_DECIMAUX, 3);
    const nonDecItems = nonDec.map(nd => fracTex(nd.num, nd.den));

    return makeClasser(
        [
            { name: 'Nombre décimal', items: decItems },
            { name: 'Nombre non décimal', items: nonDecItems },
        ],
        '<p>Classe ces nombres en deux catégories.</p>',
        opts(L3),
    );
};

// ─── Registre ───
const GENERATORS = {
    convertir_frac_qcm_unique: genConvertirFracQcm,
    convertir_frac_texte: genConvertirFracSaisie,
    convertir_frac_tableau_vf: genConvertirFracVF,
    convertir_frac_relier: genConvertirFracRelier,
    convertir_dec_qcm_unique: genConvertirDecQcm,
    convertir_dec_texte_trou_saisie: genConvertirDecSaisie,
    convertir_dec_texte_trou_drag: genConvertirDecDrag,
    identifier_qcm_multiple: genIdentifierQcmMulti,
    identifier_classer: genIdentifierClasser,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'convertir_frac', label: L1, formatsList: buildFormats('convertir_frac') },
    { id: 'convertir_dec', label: L2, formatsList: buildFormats('convertir_dec') },
    { id: 'identifier', label: L3, formatsList: buildFormats('identifier') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
